// =============================================================================
// components/KpiIncidents/IncidentFilters.jsx
//
// Toolbar densa de filtros — minimalista, sem header dramatico.
// Todos os filtros sao client-side (resposta instantanea).
// =============================================================================
import {
	Box, Paper, FormControl, InputLabel, Select, MenuItem,
	TextField, Button, Chip, Tooltip, InputAdornment, IconButton,
} from '@mui/material';
import RefreshIcon       from '@mui/icons-material/Refresh';
import FilterAltOffIcon  from '@mui/icons-material/FilterAltOff';
import SearchIcon        from '@mui/icons-material/Search';
import {
	STATUS_OPTIONS, SEVERITY_OPTIONS, DURATION_OPTIONS,
} from '../../constants/kpiConstants';

function countActiveFilters(filters) {
	let n = 0;
	if (filters.status   !== 'Open') n += 1;
	if (filters.tech     !== 'all')  n += 1;
	if (filters.severity !== 'all')  n += 1;
	if (filters.vendor   !== 'all')  n += 1;
	if (filters.kpiName  !== 'all')  n += 1;
	if (filters.siteId   !== '')     n += 1;
	if (filters.cellId   !== '')     n += 1;
	if (filters.durationBucket !== 'all') n += 1;
	return n;
}

export default function IncidentFilters({
	filters, availableTechs, availableVendors, availableKpis,
	onChange, onReset, onRefresh, loading,
}) {
	const activeCount = countActiveFilters(filters);
	const hasActive   = activeCount > 0;

	const handle = function (field) {
		return function (e) {
			let value = e.target.value;
			if (field === 'severity' && value !== 'all') value = Number(value);
			const patch = {};
			patch[field] = value;
			onChange(patch);
		};
	};

	return (
		<Paper variant="outlined" sx={{ p: 1.25 }}>
			<Box sx={{
				display: 'flex', alignItems: 'center',
				gap: 1, flexWrap: 'wrap',
			}}>
				{/* Status */}
				<FormControl size="small" sx={{ minWidth: 110 }}>
					<InputLabel>Estado</InputLabel>
					<Select value={filters.status} label="Estado" onChange={handle('status')}>
						<MenuItem value="all">Todos</MenuItem>
						{STATUS_OPTIONS.map(function (s) {
							return <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>;
						})}
					</Select>
				</FormControl>

				{/* Tech */}
				<FormControl size="small" sx={{ minWidth: 100 }}>
					<InputLabel>Tech</InputLabel>
					<Select value={filters.tech} label="Tech" onChange={handle('tech')}>
						<MenuItem value="all">Todas</MenuItem>
						{availableTechs.map(function (t) {
							return <MenuItem key={t} value={t}>{t}</MenuItem>;
						})}
					</Select>
				</FormControl>

				{/* Severity */}
				<FormControl size="small" sx={{ minWidth: 125 }}>
					<InputLabel>Severidade</InputLabel>
					<Select value={filters.severity} label="Severidade" onChange={handle('severity')}>
						<MenuItem value="all">Todas</MenuItem>
						{SEVERITY_OPTIONS.map(function (s) {
							return (
								<MenuItem key={s.value} value={s.value}>
									<Box component="span" sx={{
										display: 'inline-block', width: 7, height: 7,
										borderRadius: '50%', bgcolor: s.color, mr: 1,
									}} />
									{s.label}
								</MenuItem>
							);
						})}
					</Select>
				</FormControl>

				{/* Duration */}
				<FormControl size="small" sx={{ minWidth: 145 }}>
					<InputLabel>Duracao</InputLabel>
					<Select value={filters.durationBucket} label="Duracao" onChange={handle('durationBucket')}>
						{DURATION_OPTIONS.map(function (d) {
							return <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>;
						})}
					</Select>
				</FormControl>

				{/* KPI (grows) */}
				<FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
					<InputLabel>KPI</InputLabel>
					<Select value={filters.kpiName} label="KPI" onChange={handle('kpiName')}>
						<MenuItem value="all">Todos os KPIs</MenuItem>
						{availableKpis.map(function (k) {
							return <MenuItem key={k} value={k}>{k}</MenuItem>;
						})}
					</Select>
				</FormControl>

				{/* Vendor */}
				<FormControl size="small" sx={{ minWidth: 125 }}>
					<InputLabel>Vendor</InputLabel>
					<Select value={filters.vendor} label="Vendor" onChange={handle('vendor')}>
						<MenuItem value="all">Todos</MenuItem>
						{availableVendors.map(function (v) {
							return <MenuItem key={v} value={v}>{v}</MenuItem>;
						})}
					</Select>
				</FormControl>

				{/* Site search */}
				<TextField
					size="small"
					placeholder="Site ID"
					value={filters.siteId}
					onChange={handle('siteId')}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
							</InputAdornment>
						),
					}}
					sx={{ width: 140 }}
				/>

				{/* Cell search */}
				<TextField
					size="small"
					placeholder="Cell ID"
					value={filters.cellId}
					onChange={handle('cellId')}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
							</InputAdornment>
						),
					}}
					sx={{ width: 140 }}
				/>

				<Box sx={{ flex: 1 }} />

				{/* Active filters chip + reset */}
				{hasActive && (
					<>
						<Chip
							label={activeCount + ' filtro' + (activeCount === 1 ? '' : 's')}
							size="small"
							color="primary"
							variant="outlined"
							sx={{ height: 22, fontSize: '0.7rem' }}
						/>
						<Tooltip title="Limpar filtros">
							<IconButton size="small" onClick={onReset}>
								<FilterAltOffIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</>
				)}

				<Tooltip title="Recarregar do servidor">
					<span>
						<IconButton
							size="small"
							onClick={onRefresh}
							disabled={loading}
							sx={{ border: '1px solid', borderColor: 'divider' }}
						>
							<RefreshIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</Box>
		</Paper>
	);
}
